<template>
  <aside class="sidebar" :class="{ collapsed: isCollapsed }" :style="sidebarStyle">
    <div class="sidebar-header">
      <!-- Dynamic Logo -->
      <div v-if="!isCollapsed" class="sidebar-logo">
        <img 
          v-if="brandingLogo && !logoError" 
          :src="brandingLogo" 
          :alt="appName"
          @error="handleLogoError"
        />
        <h2 v-else class="text-logo">{{ appName }}</h2>
      </div>
      <button @click="toggleSidebar" class="toggle-btn">
        <i :class="isCollapsed ? 'fas fa-chevron-right' : 'fas fa-chevron-left'"></i>
      </button>
    </div>
    
    <nav class="sidebar-nav">
      <!-- ADI hub-and-spoke: sparse landmarks (reports live in ComplianceSpokeNav) -->
      <template v-if="useAdiHubSpokeNav">
        <div class="nav-section">
          <router-link
            class="nav-item"
            active-class=""
            exact-active-class=""
            :class="{ active: isHomeLandmarkActive }"
            :to="homeLandmarkTo"
          >
            <i class="fas fa-home"></i>
            <span v-if="!isCollapsed">Home</span>
          </router-link>

          <template v-if="isLearningPortalLandmarks">
            <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'HEALTHCARE_ADMIN', 'COMPLIANCE_ADMIN', 'ADMIN', 'LEARNER', 'SPEAKER', 'LMS_ADMIN']">
              <router-link
                :to="{ path: '/learning', query: { tab: 'browse' } }"
                class="nav-item"
                active-class=""
                exact-active-class=""
                :class="{ active: isLearningLandmarkActive }"
              >
                <i class="fas fa-graduation-cap"></i>
                <span v-if="!isCollapsed">Learning</span>
              </router-link>
            </RoleGuard>

            <router-link
              :to="{ path: '/learning', query: { tab: 'agent' } }"
              class="nav-item"
              active-class=""
              exact-active-class=""
              :class="{ active: isAgentLandmarkActive }"
            >
              <i class="fas fa-stethoscope"></i>
              <span v-if="!isCollapsed">Vet Consult</span>
            </router-link>
          </template>

          <template v-else>
          <router-link
            to="/compliance-health"
            class="nav-item"
            active-class=""
            exact-active-class=""
            :class="{ active: isComplianceLandmarkActive }"
          >
            <i class="fas fa-heartbeat"></i>
            <span v-if="!isCollapsed">Compliance</span>
          </router-link>

          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'HEALTHCARE_ADMIN', 'COMPLIANCE_ADMIN', 'ANALYST', 'ADMIN', 'RISK_MANAGER', 'CLIENT_MANAGER', 'COMPLIANCE_OPS']">
            <router-link
              to="/clients"
              class="nav-item"
              active-class=""
              exact-active-class=""
              :class="{ active: isPortfolioLandmarkActive }"
            >
              <i class="fas fa-briefcase"></i>
              <span v-if="!isCollapsed">Portfolio</span>
            </router-link>
          </RoleGuard>

          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'HEALTHCARE_ADMIN', 'COMPLIANCE_ADMIN', 'ADMIN', 'LEARNER', 'SPEAKER', 'LMS_ADMIN']">
            <router-link
              :to="{ path: '/learning', query: { tab: 'browse' } }"
              class="nav-item"
              active-class=""
              exact-active-class=""
              :class="{ active: $route.path.startsWith('/learning') }"
            >
              <i class="fas fa-graduation-cap"></i>
              <span v-if="!isCollapsed">Learning</span>
            </router-link>
          </RoleGuard>

          <router-link
            to="/compliance-health/consult"
            class="nav-item"
            active-class=""
            exact-active-class=""
            :class="{ active: isAgentLandmarkActive }"
          >
            <i class="fas fa-comments"></i>
            <span v-if="!isCollapsed">Agent</span>
          </router-link>

          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'HEALTHCARE_ADMIN', 'COMPLIANCE_ADMIN', 'ADMIN', 'BUILDER']">
            <router-link
              to="/build-management"
              class="nav-item"
              active-class=""
              exact-active-class=""
              :class="{ active: isBuildLandmarkActive }"
            >
              <i class="fas fa-hard-hat"></i>
              <span v-if="!isCollapsed">Build</span>
            </router-link>
          </RoleGuard>
          </template>
        </div>

        <div class="nav-section">
          <router-link
            to="/settings/profile"
            class="nav-item"
            active-class=""
            exact-active-class=""
            :class="{ active: isAccountLandmarkActive }"
          >
            <i class="fas fa-user-circle"></i>
            <span v-if="!isCollapsed">Account</span>
          </router-link>
          <RoleGuard require-admin>
            <router-link to="/settings/organization" class="nav-item sub-item">
              <i class="fas fa-building-user"></i>
              <span v-if="!isCollapsed">Organization</span>
            </router-link>
            <router-link to="/settings/users" class="nav-item sub-item">
              <i class="fas fa-user-cog"></i>
              <span v-if="!isCollapsed">Users</span>
            </router-link>
          </RoleGuard>
          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'HEALTHCARE_ADMIN', 'COMPLIANCE_ADMIN', 'ADMIN', 'COMPLIANCE_OPS', 'LMS_ADMIN']">
            <router-link
              to="/settings/marketing"
              class="nav-item sub-item"
              active-class=""
              exact-active-class=""
              :class="{ active: isMarketingActive }"
            >
              <i class="fas fa-bullhorn"></i>
              <span v-if="!isCollapsed">Marketing</span>
            </router-link>
          </RoleGuard>
          <RoleGuard require-platform>
            <router-link
              to="/settings/platform"
              class="nav-item sub-item"
              active-class=""
              exact-active-class=""
              :class="{ active: $route.path.startsWith('/settings/platform') }"
            >
              <i class="fas fa-cogs"></i>
              <span v-if="!isCollapsed">Platform</span>
            </router-link>
          </RoleGuard>
          <RoleGuard require-admin>
            <router-link
              to="/settings/branding"
              class="nav-item sub-item"
              active-class=""
              exact-active-class=""
              :class="{ active: $route.path.startsWith('/settings/branding') }"
            >
              <i class="fas fa-palette"></i>
              <span v-if="!isCollapsed">Branding</span>
            </router-link>
            <router-link
              to="/settings/billing"
              class="nav-item sub-item"
              active-class=""
              exact-active-class=""
              :class="{ active: $route.path.startsWith('/settings/billing') }"
            >
              <i class="fas fa-credit-card"></i>
              <span v-if="!isCollapsed">Billing</span>
            </router-link>
          </RoleGuard>
          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'COMPLIANCE_ADMIN', 'ADMIN']">
            <router-link
              to="/invoicing"
              class="nav-item sub-item"
              active-class=""
              exact-active-class=""
              :class="{ active: isInvoicingActive }"
            >
              <i class="fas fa-receipt"></i>
              <span v-if="!isCollapsed">Invoices</span>
            </router-link>
          </RoleGuard>
          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ADMIN']">
            <router-link
              to="/expenses"
              class="nav-item sub-item"
              active-class=""
              exact-active-class=""
              :class="{ active: isExpensesActive }"
            >
              <i class="fas fa-file-invoice-dollar"></i>
              <span v-if="!isCollapsed">Business Expenses</span>
            </router-link>
          </RoleGuard>
        </div>
      </template>

      <template v-else>
      <!-- COMPLIANCE_OPS — simplified ADI partner navigation -->
      <RoleGuard :roles="['COMPLIANCE_OPS']">
        <div class="nav-section">
          <h3 class="nav-section-title" v-if="!isCollapsed">🛡️ COMPLIANCE OPERATIONS</h3>

          <router-link to="/clients" class="nav-item">
            <i class="fas fa-home"></i>
            <span v-if="!isCollapsed">Home</span>
          </router-link>

          <router-link to="/coi/integrations" class="nav-item">
            <i class="fas fa-plug"></i>
            <span v-if="!isCollapsed">Integrations</span>
          </router-link>

          <router-link to="/compliance-health" class="nav-item">
            <i class="fas fa-heartbeat"></i>
            <span v-if="!isCollapsed">Compliance Health</span>
          </router-link>

          <router-link
            v-if="franchiseHubEnabled && franchiseHubProjectId"
            :to="`/projects/${franchiseHubProjectId}/franchise-data`"
            class="nav-item"
          >
            <i class="fas fa-store"></i>
            <span v-if="!isCollapsed">Franchise Data</span>
          </router-link>

          <router-link to="/clients/all" class="nav-item">
            <i class="fas fa-users"></i>
            <span v-if="!isCollapsed">Clients</span>
          </router-link>

          <router-link to="/projects" class="nav-item">
            <i class="fas fa-folder"></i>
            <span v-if="!isCollapsed">Projects & Check Lab</span>
          </router-link>

          <router-link to="/client-management" class="nav-item">
            <i class="fas fa-users-cog"></i>
            <span v-if="!isCollapsed">Client Management</span>
          </router-link>

          <router-link to="/agent-activity" class="nav-item" title="Hot leads from insurance agents">
            <i class="fas fa-bolt"></i>
            <span v-if="!isCollapsed">Hot Leads</span>
          </router-link>

          <router-link to="/growth-contacts" class="nav-item">
            <i class="fas fa-address-book"></i>
            <span v-if="!isCollapsed">Growth Contacts</span>
          </router-link>

          <router-link to="/workflow-from-document" class="nav-item">
            <i class="fas fa-robot"></i>
            <span v-if="!isCollapsed">AI Check Builder</span>
          </router-link>

          <router-link to="/prompt-enhancement" class="nav-item">
            <i class="fas fa-fire"></i>
            <span v-if="!isCollapsed">Prompt Enhancement</span>
          </router-link>

          <router-link to="/compliance-health/consult" class="nav-item">
            <i class="fas fa-comments"></i>
            <span v-if="!isCollapsed">Ask the Agent</span>
          </router-link>
        </div>

        <div class="nav-section">
          <h3 class="nav-section-title" v-if="!isCollapsed">👤 MY ACCOUNT</h3>
          <router-link to="/settings/profile" class="nav-item">
            <i class="fas fa-user-circle"></i>
            <span v-if="!isCollapsed">Profile & Password</span>
          </router-link>
        </div>
      </RoleGuard>

      <!-- Standard navigation — hidden for COMPLIANCE_OPS users -->
      <RoleGuard :roles="['COMPLIANCE_OPS']" :not="true">
      <!-- Client & Project Management - Compliance admins and analysts -->
      <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'HEALTHCARE_ADMIN', 'COMPLIANCE_ADMIN', 'ANALYST', 'ADMIN', 'RISK_MANAGER', 'CLIENT_MANAGER']">
        <div class="nav-section">
          <h3 class="nav-section-title" v-if="!isCollapsed">👥 CLIENT & PROJECT MANAGEMENT</h3>
          
          <router-link to="/clients" class="nav-item">
            <i class="fas fa-users"></i>
            <span v-if="!isCollapsed">Clients</span>
          </router-link>

          <router-link to="/projects" class="nav-item">
            <i class="fas fa-folder"></i>
            <span v-if="!isCollapsed">Projects</span>
          </router-link>

          <router-link to="/intake-queue" class="nav-item">
            <i class="fas fa-inbox"></i>
            <span v-if="!isCollapsed">Document Intake</span>
          </router-link>
        </div>
      </RoleGuard>

      <!-- Learning Management - Internal users, learners, and CE speakers -->
      <!-- SPEAKER added so faculty users see the LMS but NOT the admin items
           below (those keep require-admin) — Manage Courses & Learner
           Engagement remain admin-only. Speakers also get the Vet Consult
           AI assistant under this section so the entire SPEAKER nav lives
           in one place. -->
      <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'HEALTHCARE_ADMIN', 'COMPLIANCE_ADMIN', 'ADMIN', 'LEARNER', 'SPEAKER']">
        <div class="nav-section">
          <h3 class="nav-section-title" v-if="!isCollapsed">📚 LEARNING MANAGEMENT</h3>
          
          <router-link
            :to="{ path: '/learning', query: { tab: 'browse' } }"
            class="nav-item"
          >
            <i class="fas fa-book-open"></i>
            <span v-if="!isCollapsed">Course Catalog</span>
          </router-link>

          <router-link to="/learning/my-courses" class="nav-item">
            <i class="fas fa-graduation-cap"></i>
            <span v-if="!isCollapsed">My Courses</span>
          </router-link>

          <router-link to="/learning/my-library" class="nav-item">
            <i class="fas fa-bookmark"></i>
            <span v-if="!isCollapsed">My Library</span>
          </router-link>

          <router-link
            :to="{ path: '/learning', query: { tab: 'agent' } }"
            class="nav-item"
          >
            <i class="fas fa-stethoscope"></i>
            <span v-if="!isCollapsed">Vet Consult</span>
          </router-link>

          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'LMS_ADMIN', 'ADMIN']">
            <router-link to="/learning/admin" class="nav-item">
              <i class="fas fa-cog"></i>
              <span v-if="!isCollapsed">Manage Courses</span>
            </router-link>
          </RoleGuard>
          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'LMS_ADMIN', 'ADMIN']">
            <router-link to="/learning/engagement" class="nav-item">
              <i class="fas fa-chart-line"></i>
              <span v-if="!isCollapsed">Learner Engagement</span>
            </router-link>
          </RoleGuard>
        </div>
      </RoleGuard>

      <!-- Optimization & Analytics - Platform staff + Healthcare admins -->
      <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'HEALTHCARE_ADMIN']">
        <div class="nav-section">
          <h3 class="nav-section-title" v-if="!isCollapsed">🧪 OPTIMIZATION & ANALYTICS</h3>

          <!-- Platform-only internal tools -->
          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER']">
            <router-link to="/ab-testing" class="nav-item">
              <i class="fas fa-flask"></i>
              <span v-if="!isCollapsed">A/B Testing</span>
            </router-link>

            <router-link to="/database-connections" class="nav-item">
              <i class="fas fa-database"></i>
              <span v-if="!isCollapsed">Database Connections</span>
            </router-link>

            <router-link to="/api-integrations" class="nav-item">
              <i class="fas fa-plug"></i>
              <span v-if="!isCollapsed">API Integrations</span>
            </router-link>
          </RoleGuard>

          <!-- Regulatory & policy tools - visible to Healthcare admins -->
          <router-link to="/regulatory-intelligence" class="nav-item">
            <i class="fas fa-compass"></i>
            <span v-if="!isCollapsed">Regulatory Intelligence</span>
          </router-link>

          <router-link to="/regulatory/saved-views" class="nav-item">
            <i class="fas fa-bookmark"></i>
            <span v-if="!isCollapsed">Saved Views</span>
          </router-link>

          <router-link to="/regulatory/alerts" class="nav-item">
            <i class="fas fa-bell"></i>
            <span v-if="!isCollapsed">Alerts</span>
            <span v-if="unreadAlertsCount > 0" class="nav-badge">{{ unreadAlertsCount }}</span>
          </router-link>

          <router-link to="/policy-monitor" class="nav-item">
            <i class="fas fa-satellite-dish"></i>
            <span v-if="!isCollapsed">Policy Monitor</span>
          </router-link>

          <router-link to="/source-catalog" class="nav-item">
            <i class="fas fa-book-medical"></i>
            <span v-if="!isCollapsed">Source Catalog</span>
          </router-link>

          <router-link to="/data-subscriptions" class="nav-item">
            <i class="fas fa-rss"></i>
            <span v-if="!isCollapsed">Data Subscriptions</span>
          </router-link>
        </div>
      </RoleGuard>

      <!-- Compliance Intelligence -->
      <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'HEALTHCARE_ADMIN', 'COMPLIANCE_ADMIN', 'ANALYST', 'ADMIN', 'RISK_MANAGER', 'CLIENT_MANAGER']">
        <div class="nav-section">
          <h3 class="nav-section-title" v-if="!isCollapsed">🧠 COMPLIANCE INTELLIGENCE</h3>
          
          <!-- Project 68 Processing - Internal only (not for client-facing ANALYST) -->
          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'HEALTHCARE_ADMIN', 'COMPLIANCE_ADMIN', 'ADMIN']">
            <router-link to="/project68/processing" class="nav-item">
              <i class="fas fa-cogs"></i>
              <span v-if="!isCollapsed">Project 68 Processing</span>
            </router-link>
          </RoleGuard>

          <!-- Data Import & Onboarding - PLATFORM ONLY (proprietary feature) -->
          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER']">
            <a @click="toggleOnboardingMenu" class="nav-item has-submenu">
              <i class="fas fa-upload"></i>
              <span v-if="!isCollapsed">Data Import & Onboarding</span>
              <i :class="onboardingMenuOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" class="submenu-arrow" v-if="!isCollapsed"></i>
            </a>
            <div v-show="onboardingMenuOpen || isCollapsed" class="submenu">
              <router-link to="/coi-import" class="nav-item sub-item" title="Import compliance subjects from Certificate of Insurance documents">
                <i class="fas fa-file-import"></i>
                <span v-if="!isCollapsed">Import from COI</span>
              </router-link>
              <router-link to="/import-onboarding" class="nav-item sub-item" title="CSV/Excel import wizard for subject onboarding">
                <i class="fas fa-file-excel"></i>
                <span v-if="!isCollapsed">Import Wizard</span>
              </router-link>
              <router-link to="/batch-processing" class="nav-item sub-item" title="Large-scale batch processing for comprehensive analysis">
                <i class="fas fa-tasks"></i>
                <span v-if="!isCollapsed">Batch Processing</span>
              </router-link>
            </div>
          </RoleGuard>
          
          <router-link to="/clients" class="nav-item">
            <i class="fas fa-home"></i>
            <span v-if="!isCollapsed">Home</span>
          </router-link>

          <router-link to="/coi/integrations" class="nav-item">
            <i class="fas fa-plug"></i>
            <span v-if="!isCollapsed">Integrations</span>
          </router-link>

          <router-link to="/compliance-health" class="nav-item">
            <i class="fas fa-heartbeat"></i>
            <span v-if="!isCollapsed">Compliance Health</span>
          </router-link>

          <h4 v-if="!isCollapsed" class="nav-subsection-title">Operations</h4>
          <router-link
            v-if="franchiseHubEnabled && franchiseHubProjectId"
            :to="`/projects/${franchiseHubProjectId}/franchise-data`"
            class="nav-item sub-item"
          >
            <i class="fas fa-store"></i>
            <span v-if="!isCollapsed">Franchise Data</span>
          </router-link>
          <router-link
            v-if="!franchiseHubEnabled"
            to="/active-locations-report"
            class="nav-item sub-item"
          >
            <i class="fas fa-map-marker-alt"></i>
            <span v-if="!isCollapsed">Active Locations (FC)</span>
          </router-link>
          <router-link to="/compliance-report" class="nav-item sub-item">
            <i class="fas fa-chart-bar"></i>
            <span v-if="!isCollapsed">Live Compliance Report</span>
          </router-link>
          <router-link to="/compliance-responsiveness" class="nav-item sub-item">
            <i class="fas fa-stopwatch"></i>
            <span v-if="!isCollapsed">Responsiveness & Locations</span>
          </router-link>

          <h4 v-if="!isCollapsed" class="nav-subsection-title">Growth</h4>
          <RoleGuard require-platform-admin>
            <router-link to="/agent-activity" class="nav-item sub-item" title="Live feed of insurance-agent COI engagement (internal)">
              <i class="fas fa-bolt"></i>
              <span v-if="!isCollapsed">Agent Activity</span>
            </router-link>
          </RoleGuard>
          <RoleGuard require-platform-admin>
            <router-link to="/growth-contacts" class="nav-item sub-item">
              <i class="fas fa-address-book"></i>
              <span v-if="!isCollapsed">Growth Contacts</span>
            </router-link>
          </RoleGuard>

          <router-link to="/compliance-health/consult" class="nav-item sub-item" title="Ask the compliance agent — full-page chat with sessions, evidence, and saved consults">
            <i class="fas fa-comments"></i>
            <span v-if="!isCollapsed">Ask the Agent</span>
          </router-link>

          <h4 v-if="!isCollapsed" class="nav-subsection-title">Admin</h4>
          <RoleGuard require-platform-admin>
            <router-link to="/client-management" class="nav-item sub-item">
              <i class="fas fa-users-cog"></i>
              <span v-if="!isCollapsed">Client Management</span>
            </router-link>
          </RoleGuard>
          
          <!-- RAG Analytics - Platform and higher tier customers -->
          <RoleGuard require-feature="advanced_analytics">
            <router-link to="/rag-analytics-demo" class="nav-item">
              <i class="fas fa-chart-line"></i>
              <span v-if="!isCollapsed">RAG Analytics</span>
            </router-link>
          </RoleGuard>
          
          <!-- RAG Admin Dashboard - Admin only -->
          <RoleGuard :require-admin="true">
            <router-link to="/rag-admin-dashboard" class="nav-item">
              <i class="fas fa-cogs"></i>
              <span v-if="!isCollapsed">RAG Admin</span>
            </router-link>
          </RoleGuard>
          
          <router-link to="/compliance-report" class="nav-item" style="display: none;">
            <i class="fas fa-chart-bar"></i>
            <span v-if="!isCollapsed">Live Compliance Report</span>
          </router-link>
          <router-link to="/compliance-responsiveness" class="nav-item" style="display: none;">
            <i class="fas fa-stopwatch"></i>
            <span v-if="!isCollapsed">Responsiveness & Locations</span>
          </router-link>
          <router-link to="/active-locations-report" class="nav-item" style="display: none;">
            <i class="fas fa-map-marker-alt"></i>
            <span v-if="!isCollapsed">Active Locations (FC)</span>
          </router-link>

          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'ADMIN']">
            <router-link
              to="/wealth/equity-screens"
              class="nav-item"
              title="Trailing P/E &lt; 40 screens on S&amp;P 500 / 400 / 600"
            >
              <i class="fas fa-balance-scale"></i>
              <span v-if="!isCollapsed">Equity Screens</span>
            </router-link>
          </RoleGuard>
          <router-link to="/carrier-intelligence" class="nav-item">
            <i class="fas fa-building"></i>
            <span v-if="!isCollapsed">Carrier Intelligence</span>
          </router-link>
          <router-link to="/carrier-analytics" class="nav-item">
            <i class="fas fa-chart-pie"></i>
            <span v-if="!isCollapsed">Carrier Analytics</span>
          </router-link>
          
          <!-- Research Campaigns - Internal only -->
          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'HEALTHCARE_ADMIN', 'COMPLIANCE_ADMIN', 'ADMIN']">
            <router-link to="/campaigns" class="nav-item">
              <i class="fas fa-rocket"></i>
              <span v-if="!isCollapsed">Research Campaigns</span>
            </router-link>
          </RoleGuard>

          <!-- Quote Builder - Internal only (not for client-facing ANALYST) -->
          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'HEALTHCARE_ADMIN', 'COMPLIANCE_ADMIN', 'ADMIN']">
            <router-link to="/quote-builder" class="nav-item">
              <i class="fas fa-file-invoice-dollar"></i>
              <span v-if="!isCollapsed">Quote Builder</span>
            </router-link>
            
            <router-link to="/quotes" class="nav-item">
              <i class="fas fa-folder-open"></i>
              <span v-if="!isCollapsed">Saved Quotes</span>
            </router-link>
          </RoleGuard>

          <!-- Invoicing -->
          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'COMPLIANCE_ADMIN', 'ADMIN']">
            <router-link to="/invoicing" class="nav-item">
              <i class="fas fa-receipt"></i>
              <span v-if="!isCollapsed">Invoices</span>
            </router-link>
          </RoleGuard>

          <!-- Business Expenses (owner reimbursement) -->
          <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ADMIN']">
            <router-link to="/expenses" class="nav-item">
              <i class="fas fa-file-invoice-dollar"></i>
              <span v-if="!isCollapsed">Business Expenses</span>
            </router-link>
          </RoleGuard>
        </div>
      </RoleGuard>

      <!-- Real Estate / Build Management -->
      <RoleGuard :roles="['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'HEALTHCARE_ADMIN', 'COMPLIANCE_ADMIN', 'ADMIN', 'BUILDER']">
        <div class="nav-section">
          <h3 class="nav-section-title" v-if="!isCollapsed">🏠 BUILD MANAGEMENT</h3>
          
          <router-link to="/build-management" class="nav-item">
            <i class="fas fa-hard-hat"></i>
            <span v-if="!isCollapsed">Build Dashboard</span>
          </router-link>
          
          <router-link to="/knowledge-graph" class="nav-item">
            <i class="fas fa-project-diagram"></i>
            <span v-if="!isCollapsed">Knowledge Graph</span>
          </router-link>
          
          <router-link to="/construction-dashboard" class="nav-item">
            <i class="fas fa-hard-hat"></i>
            <span v-if="!isCollapsed">Construction Dashboard</span>
          </router-link>
          
          <router-link to="/house-pricing" class="nav-item">
            <i class="fas fa-calculator"></i>
            <span v-if="!isCollapsed">House Pricing</span>
          </router-link>

          <router-link to="/development-budgets" class="nav-item">
            <i class="fas fa-chart-bar"></i>
            <span v-if="!isCollapsed">Elevation Budgets</span>
          </router-link>

          <router-link to="/gap-resolution" class="nav-item">
            <i class="fas fa-search-dollar"></i>
            <span v-if="!isCollapsed">Gap Resolution</span>
          </router-link>

          <router-link to="/construction-wip" class="nav-item">
            <i class="fas fa-tasks"></i>
            <span v-if="!isCollapsed">WIP Board</span>
          </router-link>

          <router-link to="/buyer-portal" class="nav-item">
            <i class="fas fa-palette"></i>
            <span v-if="!isCollapsed">Buyer Selections</span>
          </router-link>
          
          <router-link to="/database-connections" class="nav-item">
            <i class="fas fa-database"></i>
            <span v-if="!isCollapsed">Data Warehouse</span>
          </router-link>
          
          <router-link to="/duplicate-detection" class="nav-item">
            <i class="fas fa-copy"></i>
            <span v-if="!isCollapsed">Duplicate Detection</span>
          </router-link>
        </div>
      </RoleGuard>

      <!-- Administration -->
      <RoleGuard require-admin>
        <div class="nav-section administration-section">
          <h3 class="nav-section-title" v-if="!isCollapsed">⚙️ ADMINISTRATION</h3>
          
          <router-link to="/workflow-from-document" class="nav-item">
            <i class="fas fa-robot"></i>
            <span v-if="!isCollapsed">AI Check Builder</span>
          </router-link>
          
          <router-link to="/prompt-enhancement" class="nav-item">
            <i class="fas fa-fire"></i>
            <span v-if="!isCollapsed">Prompt Enhancement</span>
          </router-link>
        </div>
      </RoleGuard>

      <!-- Platform Administration (Platform users only) -->
      <RoleGuard require-platform>
        <div class="nav-section administration-section">
          <h3 class="nav-section-title" v-if="!isCollapsed">🚀 PLATFORM ADMINISTRATION</h3>
          
          <router-link to="/settings/platform" class="nav-item">
            <i class="fas fa-cogs"></i>
            <span v-if="!isCollapsed">Platform Settings</span>
          </router-link>
        </div>
      </RoleGuard>

      <!-- Organization Settings -->
      <RoleGuard require-admin>
        <div class="nav-section administration-section">
          <h3 class="nav-section-title" v-if="!isCollapsed">🏢 ORGANIZATION SETTINGS</h3>
          
          <router-link to="/settings/organization" class="nav-item">
            <i class="fas fa-building-user"></i>
            <span v-if="!isCollapsed">Organization Settings</span>
          </router-link>
          
          <router-link to="/settings/branding" class="nav-item">
            <i class="fas fa-palette"></i>
            <span v-if="!isCollapsed">Branding & Themes</span>
          </router-link>

          <router-link to="/settings/email-branding" class="nav-item">
            <i class="fas fa-envelope-open-text"></i>
            <span v-if="!isCollapsed">Email Branding</span>
          </router-link>

          <router-link to="/settings/users" class="nav-item">
            <i class="fas fa-user-cog"></i>
            <span v-if="!isCollapsed">User Management</span>
          </router-link>
          
          <router-link to="/settings/billing" class="nav-item">
            <i class="fas fa-credit-card"></i>
            <span v-if="!isCollapsed">Billing & Subscription</span>
          </router-link>
          
          <router-link to="/settings/payments" class="nav-item">
            <i class="fas fa-university"></i>
            <span v-if="!isCollapsed">Payment Processing</span>
          </router-link>
        </div>
      </RoleGuard>

      <!-- My Account - visible to roles that don't already get profile via
           the Organization Settings block (BUILDER, SPEAKER, LEARNER). Lets
           them change their temporary password after first login. -->
      <RoleGuard :roles="['BUILDER', 'SPEAKER', 'LEARNER']">
        <div class="nav-section">
          <h3 class="nav-section-title" v-if="!isCollapsed">👤 MY ACCOUNT</h3>
          <router-link to="/settings/profile" class="nav-item">
            <i class="fas fa-user-circle"></i>
            <span v-if="!isCollapsed">Profile & Password</span>
          </router-link>
        </div>
      </RoleGuard>

      </RoleGuard>

      </template>

    </nav>
  </aside>
</template>

<script>
import auth from '@/services/auth';
import api from '@/services/api';
import RoleGuard from '@/components/RoleGuard.vue';
import whitelabelBranding from '@/services/whitelabelBrandingService';
import { isLearningPortalOrg, isVetMediaProductHost } from '@/utils/associationPortal';

export default {
  name: 'NavSidebar',
  components: {
    RoleGuard
  },
  data() {
    return {
      isCollapsed: false,
      complianceMenuOpen: false,
      analysisMenuOpen: false,
      onboardingMenuOpen: false,
      user: null,
      logoError: false,
      unreadAlertsCount: 0,
      jmfsFranchiseDataHubEnabled: false,
      franchiseHubEnabled: false,
      franchiseHubProjectId: null,
    };
  },
  computed: {
    isAuthenticated() {
      // Re-evaluate when route changes
      this.$route.path; 
      return auth.isAuthenticated();
    },
    userRole() {
      return auth.getUserRole();
    },
    userDisplayName() {
      return auth.getUserDisplayName();
    },
    userRoleDisplay() {
      const role = this.userRole;
      const roleMap = {
        // Platform Roles
        'PLATFORM_ADMIN': 'Platform Admin',
        'PLATFORM_ENGINEER': 'Platform Engineer',
        'COMPLIANCE_OPS': 'Compliance Operations',
        // Client Admin Roles
        'ORG_ADMIN': 'Organization Admin',
        'LMS_ADMIN': 'LMS Admin',
        'COMPLIANCE_ADMIN': 'Compliance Admin',
        // Client User Roles
        'ANALYST': 'Analyst',
        'LEARNER': 'Learner',
        'SPEAKER': 'CE Speaker',
        'VIEWER': 'Viewer',
        // Deprecated Roles
        'ADMIN': 'Admin (Legacy)',
        'RISK_MANAGER': 'Risk Manager (Legacy)',
        'CLIENT_MANAGER': 'Client Manager (Legacy)',
        'REVIEWER': 'Reviewer (Legacy)',
        'READ_ONLY': 'Read Only (Legacy)',
        'STUDENT': 'Non-Member'
      };
      return roleMap[role] || role.replace('_', ' ');
    },
    organizationName() {
      return auth.getOrganizationName();
    },
    subscriptionTier() {
      return auth.getSubscriptionTier();
    },
    isVerifiedMember() {
      return auth.isVerifiedMember();
    },
    memberDiscountPercentage() {
      return auth.getMemberDiscountPercentage();
    },
    // Whitelabel branding
    brandingLogo() {
      return whitelabelBranding.getLogo();
    },
    appName() {
      return whitelabelBranding.getAppName();
    },
    sidebarStyle() {
      return {
        '--sidebar-primary': whitelabelBranding.getColor('primary'),
        '--sidebar-bg': whitelabelBranding.getColor('surface'),
        '--sidebar-accent': whitelabelBranding.getColor('accent') || whitelabelBranding.getColor('primary'),
      };
    },
    /**
     * ADI / COI portal uses sparse hub-and-spoke landmarks.
     * LMS-only and builder-only roles keep the legacy focused rails for now.
     * @see docs/product/HUB_SPOKE_PORTAL.md
     */
    useAdiHubSpokeNav() {
      const role = this.userRole;
      return !['LEARNER', 'STUDENT', 'SPEAKER', 'BUILDER'].includes(role);
    },
    isLearningPortalLandmarks() {
      const orgSlug = whitelabelBranding.getCurrentOrganization()?.slug
        || whitelabelBranding.detectOrganizationSlug();
      return isLearningPortalOrg(orgSlug) || isVetMediaProductHost();
    },
    homeLandmarkTo() {
      if (this.isLearningPortalLandmarks) {
        return { path: '/learning', query: { tab: 'agent' } };
      }
      return '/clients';
    },
    isHomeLandmarkActive() {
      if (this.isLearningPortalLandmarks) {
        return this.isAgentLandmarkActive;
      }
      return this.isExactPath('/clients');
    },
    isLearningLandmarkActive() {
      const path = this.$route.path;
      if (!path.startsWith('/learning')) return false;
      if (path === '/learning' || path === '/learning/') {
        return this.$route.query?.tab !== 'agent';
      }
      return true;
    },
    isAgentLandmarkActive() {
      if (this.isLearningPortalLandmarks) {
        const path = this.$route.path;
        return (path === '/learning' || path === '/learning/') && this.$route.query?.tab === 'agent';
      }
      return this.$route.path.startsWith('/compliance-health/consult');
    },
    isComplianceLandmarkActive() {
      const path = this.$route.path;
      if (path.startsWith('/compliance-health/consult')) return false;
      return (
        path.startsWith('/compliance-health')
        || path.startsWith('/coi/integrations')
        || path.startsWith('/compliance-report')
        || path.startsWith('/compliance-responsiveness')
        || path.startsWith('/active-locations-report')
        || path.startsWith('/carrier-intelligence')
        || path.startsWith('/carrier-analytics')
        || path.includes('/match-hygiene')
        || path.startsWith('/project68')
        || path.includes('/franchise-data')
      );
    },
    isPortfolioLandmarkActive() {
      const path = this.$route.path;
      // Home owns the Portfolio hub (`/clients`); Portfolio lights up on deeper lists/detail.
      if (path === '/clients' || path === '/clients/') return false;
      return (
        path.startsWith('/clients')
        || path.startsWith('/projects')
        || path.startsWith('/intake-queue')
        || path.startsWith('/client-management')
      ) && !path.includes('/franchise-data');
    },
    isBuildLandmarkActive() {
      const path = this.$route.path;
      return (
        path.startsWith('/build-management')
        || path.startsWith('/construction')
        || path.startsWith('/house-pricing')
        || path.startsWith('/development-budgets')
        || path.startsWith('/gap-resolution')
        || path.startsWith('/knowledge-graph')
        || path.startsWith('/buyer-portal')
        || path.startsWith('/wealth/equity-screens')
        || path.startsWith('/loan-draws')
        || path.startsWith('/invoice-upload')
        || path.startsWith('/permit-completion')
        || path.startsWith('/master-data-audit')
        || path.startsWith('/duplicate-detection')
      );
    },
    isAccountLandmarkActive() {
      return (
        this.$route.path.startsWith('/settings')
        || this.isMarketingActive
        || this.isInvoicingActive
        || this.isExpensesActive
      );
    },
    isMarketingActive() {
      const path = this.$route.path;
      return (
        path.startsWith('/settings/marketing')
        || path.startsWith('/agent-activity')
        || path.startsWith('/growth-contacts')
        || path.startsWith('/quote-builder')
        || path.startsWith('/quotes')
        || path.startsWith('/quote-editor')
        || path.startsWith('/learning/engagement')
      );
    },
    isInvoicingActive() {
      return this.$route.path.startsWith('/invoicing');
    },
    isExpensesActive() {
      return this.$route.path.startsWith('/expenses');
    },
  },
  watch: {
    isAuthenticated(isAuth) {
      if (isAuth) {
        this.user = auth.getUser();
      } else {
        this.user = null;
      }
    }
  },
  created() {
    if (this.isAuthenticated) {
      this.user = auth.getUser();
      this.loadUnreadAlertsCount();
    }
    this.loadFeatureFlags();
  },
  methods: {
    isExactPath(path) {
      const current = this.$route.path;
      return current === path || current === `${path}/`;
    },
    async loadFeatureFlags() {
      try {
        const franchiseHubContext = (await import('@/services/franchiseHubContext')).default;
        const ctx = await franchiseHubContext.load();
        this.franchiseHubEnabled = Boolean(ctx.hub_enabled);
        this.franchiseHubProjectId = ctx.project_id;
        this.jmfsFranchiseDataHubEnabled = this.franchiseHubEnabled;
      } catch {
        this.franchiseHubEnabled = false;
        this.franchiseHubProjectId = null;
        this.jmfsFranchiseDataHubEnabled = false;
      }
    },
    async loadUnreadAlertsCount() {
      try {
        const response = await api.get('/regulatory/alerts/unread_count/');
        this.unreadAlertsCount = response.data.unread_count || 0;
      } catch (error) {
        // Silently fail - alerts badge is not critical
        console.debug('Could not load alerts count:', error);
      }
    },
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
      window.dispatchEvent(new CustomEvent('adi-sidebar-collapsed', {
        detail: { collapsed: this.isCollapsed },
      }));
    },
    handleLogoError() {
      console.warn('Logo failed to load, falling back to text logo');
      this.logoError = true;
    },
    toggleComplianceMenu() {
      this.complianceMenuOpen = !this.complianceMenuOpen;
    },
    toggleAnalysisMenu() {
      this.analysisMenuOpen = !this.analysisMenuOpen;
    },
    toggleOnboardingMenu() {
      this.onboardingMenuOpen = !this.onboardingMenuOpen;
    },
    logout() {
      // Navigate to logout page which handles OAuth provider logout
      this.$router.push('/logout');
    }
  }
};
</script>

<style scoped>
/* Main sidebar styling — neutral light rail (ADR-0018); accent on active only */
.sidebar {
  width: 250px;
  background-color: var(--p-surface-elevated, #ffffff);
  color: var(--p-ink, #1e293b);
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  transition: width 0.3s ease-in-out;
  border-right: 1px solid var(--p-border, #e2e8f0);
  z-index: 100;
}
.sidebar.collapsed {
  width: 60px;
}

/* Header */
.sidebar-header {
  padding: var(--spacing-md) var(--spacing-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid var(--p-border, #e2e8f0);
  height: 60px;
}
.sidebar.collapsed .sidebar-header {
  padding: var(--spacing-md) 0;
}

/* Whitelabel Logo Styling */
.sidebar-logo {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--spacing-sm);
}

.sidebar-logo img {
  max-width: 180px;
  max-height: 40px;
  object-fit: contain;
}

.sidebar-logo h2 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--p-ink, #1e293b);
}
.logo-link {
  display: flex;
  align-items: center;
  flex-grow: 1;
}
.logo-full {
  height: 28px;
  margin-left: var(--spacing-sm);
}
.logo-small {
  height: 30px;
}
.toggle-btn {
  background: none;
  border: none;
  color: var(--p-ink-muted, #64748b);
  cursor: pointer;
  font-size: 1.2rem;
  padding: var(--spacing-sm);
}

/* Navigation */
.sidebar-nav {
  flex-grow: 1;
  padding-top: var(--spacing-sm);
  overflow-y: auto;
  overflow-x: hidden;
  /* Custom scrollbar styling for better UX */
  scrollbar-width: thin;
  scrollbar-color: var(--p-border-strong, #cbd5e1) transparent;
}

/* Webkit scrollbar styling */
.sidebar-nav::-webkit-scrollbar {
  width: 6px;
}
.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}
.sidebar-nav::-webkit-scrollbar-thumb {
  background-color: var(--p-border-strong, #cbd5e1);
  border-radius: 3px;
}
.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background-color: var(--p-ink-muted, #94a3b8);
}
.nav-item {
  display: flex;
  align-items: center;
  padding: var(--spacing-sm) var(--spacing-md);
  margin: var(--spacing-xs) var(--spacing-sm);
  color: var(--p-ink-muted, #64748b);
  text-decoration: none;
  transition: background-color 0.2s, color 0.2s;
  white-space: nowrap;
  border-radius: var(--border-radius);
  border: none;
  background: transparent;
  width: calc(100% - 2 * var(--spacing-sm));
  box-sizing: border-box;
  cursor: pointer;
  font: inherit;
  text-align: left;
}
.nav-item i {
  margin-right: var(--spacing-md);
  width: 20px;
  text-align: center;
  font-size: 1.1rem;
}
.nav-item:hover {
  background-color: var(--p-surface-muted, #f1f5f9);
  color: var(--p-ink, #1e293b);
}
.nav-item.active {
  background-color: var(--p-accent-subtle, #eef2f8);
  color: var(--p-accent, var(--color-primary, #1e3a5f));
  font-weight: 600;
}

/* Navigation sections */
.nav-section {
  margin-bottom: var(--spacing-lg);
}

.nav-subsection-title {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--p-ink-muted, #64748b);
  margin: 0.75rem 0 0.35rem 0.75rem;
  padding: 0;
}

.nav-section-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--p-ink-muted, #64748b);
  margin: var(--spacing-md) var(--spacing-md) var(--spacing-sm) var(--spacing-md);
  padding: 0;
}

/* Administration section spacing */
.administration-section {
  margin-top: var(--spacing-xl);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--p-border, #e2e8f0);
}

/* Submenu styling */
.submenu {
  background-color: var(--p-surface-muted, #f8fafc);
  border-radius: var(--border-radius);
  margin: var(--spacing-xs) var(--spacing-sm);
  padding: var(--spacing-xs) 0;
}

.sub-item {
  margin-left: var(--spacing-lg);
  font-size: 0.9rem;
  opacity: 0.95;
}

.sub-item:hover {
  opacity: 1;
}

/* Submenu arrow and expandable items */
.has-submenu {
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.submenu-arrow {
  transition: transform 0.3s ease;
  font-size: 0.8rem;
  margin-left: auto;
  margin-right: 0;
}

.has-submenu:hover {
  background-color: var(--p-surface-muted, #f1f5f9);
}

/* Footer styling */
.sidebar-footer {
  padding: var(--spacing-md);
  border-top: 1px solid var(--p-border, #e2e8f0);
  margin-top: auto; /* Push to bottom */
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.footer-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.organization-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.organization-badge {
  display: flex;
  align-items: center;
  background-color: var(--p-surface-muted, #f1f5f9);
  border-radius: var(--border-radius);
  padding: var(--spacing-xs) var(--spacing-sm);
  gap: var(--spacing-xs);
}

.organization-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--p-ink, #1e293b);
}

.subscription-tier {
  font-size: 0.7rem;
  color: var(--p-ink-muted, #64748b);
  background-color: var(--p-border, #e2e8f0);
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  font-weight: bold;
}

.user-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.user-avatar {
  width: 30px;
  height: 30px;
  background-color: var(--p-surface-muted, #f1f5f9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: var(--p-ink-muted, #64748b);
}

.user-details {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--p-ink, #1e293b);
}

.user-role {
  font-size: 0.75rem;
  color: var(--p-ink-muted, #64748b);
}

/* Verified Member Badge */
.member-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  margin-top: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
}

.member-badge i {
  font-size: 0.65rem;
  margin-right: 0;
  width: auto;
}

.logout-btn {
  border-radius: var(--border-radius);
  transition: all 0.2s ease;
}

.logout-btn:hover {
  background-color: #fef2f2;
  color: #dc2626;
}

/* Demo link special styling */
.nav-item.demo-link {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  margin: 0.5rem 0;
  position: relative;
  overflow: hidden;
}

.nav-item.demo-link:hover {
  transform: translateX(5px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.nav-item.demo-link.router-link-exact-active {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

.nav-badge {
  background: #ef4444;
  color: white;
  font-size: 0.65rem;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  margin-left: auto;
  font-weight: bold;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
}
</style> 